import { useRef, useState, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import RATooltip from "../components/RATooltip";
import ContentBalanceChart from "../components/dashboard/ContentBalanceTrendChart";
import ListenerGroupProfile from "../components/dashboard/ListenerGroupProfile";
import RatingsTrend from "../components/dashboard/RatingsTrend";
import RetentionTrend from "../components/dashboard/RetentionTrend";
import TSLTrend from "../components/dashboard/TSLTrend";
import TopFlopBreaks from "../components/dashboard/TopFlopBreaks";
import { DashboardPanel, DescriptionWithDownloadRow } from "../components/pageSections";
import { useResolvedModules, useResolvedRadios } from "../components/HelperHooks";
import { getRadioName, mapItemType } from "../components/HelperFunctions";

import moment from "moment";

export default function DashboardRadio({
    daypartData,
    contentBalanceData,
    breaksData,
    daypartErrorCode,
    contentBalanceErrorCode,
    breaksErrorCode,
    daypartLoading,
    contentBalanceLoading,
    breaksLoading,
    formData,
}) {
    const ratingsRef = useRef(null);
    const tslRetentionRef = useRef(null);
    const retentionRef = useRef(null);
    const tslRef = useRef(null);
    const contentBalanceRef = useRef(null);
    const listenerGroupsRef = useRef(null);
    const topBreaksRef = useRef(null);
    const flopBreaksRef = useRef(null);
    const [fileName, setFileName] = useState("Content Balance");
    const resolvedRadios = useResolvedRadios(true);

    const resolvedModules = useResolvedModules();
    const ratingsEnabled = useMemo(() => resolvedModules?.includes(2), [resolvedModules]);
    const musicEnabled = useMemo(() => resolvedModules?.includes(5), [resolvedModules]);
    const contentTestingEnabled = useMemo(() => resolvedModules?.includes(3), [resolvedModules]);

    const csvHeaders = useMemo(() => {
        if (!resolvedRadios || !contentBalanceData) return;
        const [{ radios }] = contentBalanceData.content_balance;
        const [firstRadio, secondRadio] = radios;

        setFileName(`Content Balance for ${getRadioName(firstRadio.radio_id, resolvedRadios)} and ${getRadioName(secondRadio.radio_id, resolvedRadios)}`);

        const firstRadioBalances = firstRadio.balances.map(x => `${getRadioName(firstRadio.radio_id, resolvedRadios)} ${mapItemType(x.item_type)}`);
        const secondRadioBalances = secondRadio.balances.map(x => `${getRadioName(secondRadio.radio_id, resolvedRadios)} ${mapItemType(x.item_type)}`);

        return ["Date", ...firstRadioBalances, ...secondRadioBalances];
    }, [contentBalanceData, resolvedRadios]);

    const csvData = useMemo(() => {
        if (!contentBalanceData) return;

        return contentBalanceData.content_balance.map(({ date, radios }) => {
            const [firstRadio, secondRadio] = radios;

            const firstRadioBalances = firstRadio.balances.map(x => `${x.value.toFixed(2)}%`);
            const secondRadioBalances = secondRadio.balances.map(x => `${x.value.toFixed(2)}%`);
            return [moment(date).format("DD-MM-YYYY"), ...firstRadioBalances, ...secondRadioBalances];
        });
    }, [contentBalanceData]);

    return (
        <>
            {ratingsEnabled && (
                <>
                    <DashboardPanel containerRef={ratingsRef}>
                        <DashboardPanel.Title>
                            Ratings
                            <RATooltip>
                                The number you see in the graph is an average number pr. minute for the week starting with the date displayed in the graph.
                            </RATooltip>
                        </DashboardPanel.Title>
                        <DashboardPanel.Description
                            containerRef={ratingsRef}
                            fileName={`Ratings Trend - ${getRadioName(daypartData?.radio_id, resolvedRadios)} from ${moment(daypartData?.start_date).format(
                                "DD-MM-YYYY",
                            )} to ${moment(daypartData?.end_date).format("DD-MM-YYYY")}`}
                            dataReady={!!daypartData}>
                            <DataDescription>
                                <DataDescription.Station radioId={daypartData?.radio_id} />
                                <DataDescription.DateRange startDate={daypartData?.start_date} endDate={daypartData?.end_date} />
                                <DataDescription.TimeRange startTime={daypartData?.start_time} endTime={daypartData?.end_time} />
                                <DataDescription.Perspective days={daypartData?.days} />
                                <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <RatingsTrend data={daypartData?.ratings} loading={daypartLoading} error={daypartErrorCode} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                </>
            )}
            {ratingsEnabled && (
                <>
                    <DashboardPanel containerRef={tslRetentionRef} className=" d-none d-xxl-block">
                        <DashboardPanel.Title>
                            TSL & Retention
                            <RATooltip>
                                <h6>TLS trend</h6>
                                <p>
                                    TSL Average Time Spend Listening pr. session and Time Spend Listening pr. listener for the daypart. The graph includes the
                                    latest 8 weeks with available data. TSL includes sessions beginning before or ending after the daypart selected.
                                </p>
                                <h6>TSL per session</h6>
                                <p>Average listening per session.</p>
                                <h6>TSL per listener</h6>
                                <p>The total average listening minutes per week (cumulative) - this will be affected by missing days due to corrupt data.</p>
                                <h6>Retention</h6>
                                <p>
                                    The graph shows you how many of your heavy listeners return week by week in percentage. We have included the returning % of
                                    heavy listeners for the latest 8 weeks with available data for the daypart.
                                </p>
                            </RATooltip>
                        </DashboardPanel.Title>
                        <DashboardPanel.Description
                            containerRef={tslRetentionRef}
                            fileName={`TSL and Retention Trend - ${getRadioName(daypartData?.radio_id, resolvedRadios)} from ${moment(
                                daypartData?.start_date,
                            ).format("DD-MM-YYYY")} to ${moment(daypartData?.end_date).format("DD-MM-YYYY")}`}
                            dataReady={!!daypartData}>
                            <DataDescription>
                                <DataDescription.Station radioId={daypartData?.radio_id} />
                                <DataDescription.DateRange startDate={daypartData?.start_date} endDate={daypartData?.end_date} />
                                <DataDescription.TimeRange startTime={daypartData?.start_time} endTime={daypartData?.end_time} />
                                <DataDescription.Perspective days={daypartData?.days} />
                                <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <Row>
                                <Col>
                                    <TSLTrend
                                        data={{ tsl_session: daypartData?.tsl_session, tsl_listener: daypartData?.tsl_listener }}
                                        loading={daypartLoading}
                                        error={daypartErrorCode}
                                    />
                                </Col>
                                {daypartLoading ||
                                    (daypartData && daypartData.heavy_retention?.length > 0 && (
                                        <Col>
                                            <RetentionTrend data={daypartData?.heavy_retention} loading={daypartLoading} error={daypartErrorCode} />
                                        </Col>
                                    ))}
                            </Row>
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <DashboardPanel containerRef={tslRef} className=" d-xxl-none">
                        <DashboardPanel.Title>
                            TSL
                            <RATooltip>
                                TSL Average Time Spend Listening pr. session and Time Spend Listening pr. listener for the daypart. The graph includes the
                                latest 8 weeks with available data. TSL includes sessions beginning before or ending after the daypart selected.
                            </RATooltip>
                        </DashboardPanel.Title>
                        <DashboardPanel.Description containerRef={tslRef} fileName="TSL Trend" dataReady={!!daypartData}>
                            <DataDescription>
                                <DataDescription.Station radioId={daypartData?.radio_id} />
                                <DataDescription.DateRange startDate={daypartData?.start_date} endDate={daypartData?.end_date} />
                                <DataDescription.TimeRange startTime={daypartData?.start_time} endTime={daypartData?.end_time} />
                                <DataDescription.Perspective days={daypartData?.days} />
                                <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <TSLTrend
                                data={{ tsl_session: daypartData?.tsl_session, tsl_listener: daypartData?.tsl_listener }}
                                loading={daypartLoading}
                                error={daypartErrorCode}
                            />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2 d-xxl-none" />
                    {(daypartLoading || (daypartData && daypartData.heavy_retention?.length > 0)) && (
                        <DashboardPanel containerRef={retentionRef} className=" d-xxl-none">
                            <DashboardPanel.Title>
                                Retention
                                <RATooltip>
                                    The graph shows you how many of your heavy listeners return week by week in percentage. We have included the returning % of
                                    heavy listeners for the latest 8 weeks with available data for the daypart.
                                </RATooltip>
                            </DashboardPanel.Title>
                            <DashboardPanel.Description containerRef={retentionRef} fileName="Retention Trend" dataReady={!!daypartData}>
                                <DataDescription>
                                    <DataDescription.Station radioId={daypartData?.radio_id} />
                                    <DataDescription.DateRange startDate={daypartData?.start_date} endDate={daypartData?.end_date} />
                                    <DataDescription.TimeRange startTime={daypartData?.start_time} endTime={daypartData?.end_time} />
                                    <DataDescription.Perspective days={daypartData?.days} />
                                    <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                                </DataDescription>
                            </DashboardPanel.Description>
                            <DashboardPanel.DataContent>
                                <RetentionTrend data={daypartData?.heavy_retention} loading={daypartLoading} error={daypartErrorCode} />
                            </DashboardPanel.DataContent>
                        </DashboardPanel>
                    )}
                    <hr className="m-2" />
                </>
            )}
            {musicEnabled && (
                <>
                    <DashboardPanel containerRef={contentBalanceRef}>
                        <DashboardPanel.Title>
                            Content Balance
                            <RATooltip>
                                The graph shows you how many of your heavy listeners return week by week in percentage. We have included the returning % of
                                heavy listeners for the latest 8 weeks with available data for the daypart.
                            </RATooltip>
                        </DashboardPanel.Title>
                        <DescriptionWithDownloadRow csvHeaders={csvHeaders} data={csvData} filename={fileName} screenshotContainer={contentBalanceRef}>
                            <DashboardPanel.Description dataReady={!!contentBalanceData}>
                                <DataDescription>
                                    <DataDescription.Station label="Station 1 (Bar 1)" radioId={contentBalanceData?.radio_id} />
                                    <DataDescription.Station label="Station 2 (Bar 2)" radioId={contentBalanceData?.competitor_radio_id} />
                                    <DataDescription.DateRange startDate={contentBalanceData?.start_date} endDate={contentBalanceData?.end_date} />
                                    <DataDescription.TimeRange startTime={contentBalanceData?.start_time} endTime={contentBalanceData?.end_time} />
                                    <DataDescription.Perspective days={contentBalanceData?.days} />
                                    <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                                </DataDescription>
                            </DashboardPanel.Description>
                        </DescriptionWithDownloadRow>
                        <DashboardPanel.DataContent>
                            <ContentBalanceChart
                                data={contentBalanceData?.content_balance}
                                loading={contentBalanceLoading}
                                error={contentBalanceErrorCode}
                                grouping={contentBalanceData?.grouping}
                            />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                </>
            )}
            {ratingsEnabled && (daypartLoading || (daypartData && !!daypartData.profiles.length)) && (
                <>
                    <DashboardPanel containerRef={listenerGroupsRef}>
                        <DashboardPanel.Title>Listener Groups Profiles</DashboardPanel.Title>
                        <DashboardPanel.Description
                            containerRef={listenerGroupsRef}
                            fileName={`Listener Group Profiles - ${getRadioName(daypartData?.radio_id, resolvedRadios)} from ${moment(
                                daypartData?.start_date,
                            ).format("DD-MM-YYYY")} to ${moment(daypartData?.end_date).format("DD-MM-YYYY")}`}
                            dataReady={!!daypartData}>
                            <DataDescription>
                                <DataDescription.Station radioId={daypartData?.radio_id} />
                                <DataDescription.DateRange startDate={daypartData?.end_date} />
                                <DataDescription.TimeRange startTime={daypartData?.start_time} endTime={daypartData?.end_time} />
                                <DataDescription.Perspective days={daypartData?.days} />
                                <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <Row className=" row-cols-1 row-cols-xxl-3">
                                <Col>
                                    <ListenerGroupProfile
                                        data={daypartData?.profiles.find(x => x.listener_group === 1)}
                                        loading={daypartLoading}
                                        error={daypartErrorCode}
                                        dataType={daypartData?.data_type}
                                        header={"Light"}
                                    />
                                </Col>
                                <Col>
                                    <ListenerGroupProfile
                                        data={daypartData?.profiles.find(x => x.listener_group === 2)}
                                        loading={daypartLoading}
                                        error={daypartErrorCode}
                                        dataType={daypartData?.data_type}
                                        header={"Moderate"}
                                    />
                                </Col>
                                <Col>
                                    <ListenerGroupProfile
                                        data={daypartData?.profiles.find(x => x.listener_group === 3)}
                                        loading={daypartLoading}
                                        error={daypartErrorCode}
                                        dataType={daypartData?.data_type}
                                        header={"Heavy"}
                                    />
                                </Col>
                            </Row>
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                </>
            )}
            {contentTestingEnabled && (
                <>
                    <DashboardPanel containerRef={topBreaksRef}>
                        <DashboardPanel.Title>Top 5 Breaks</DashboardPanel.Title>
                        <DashboardPanel.Description
                            containerRef={topBreaksRef}
                            fileName={`Top 5 Breaks - ${getRadioName(breaksData?.radio_id, resolvedRadios)} from ${moment(breaksData?.start_date).format(
                                "DD-MM-YYYY",
                            )} to ${moment(breaksData?.end_date).format("DD-MM-YYYY")}`}
                            dataReady={!!breaksData}>
                            <DataDescription>
                                <DataDescription.Station radioId={breaksData?.radio_id} />
                                <DataDescription.DateRange startDate={breaksData?.start_date} endDate={breaksData?.end_date} />
                                <DataDescription.TimeRange startTime={breaksData?.start_time} endTime={breaksData?.end_time} />
                                <DataDescription.Perspective days={breaksData?.days} />
                                <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <TopFlopBreaks formData={formData} data={breaksData} loading={breaksLoading} error={breaksErrorCode} dataPath={"top_breaks"} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel containerRef={flopBreaksRef}>
                        <DashboardPanel.Title>Flop 5 Breaks</DashboardPanel.Title>
                        <DashboardPanel.Description
                            containerRef={flopBreaksRef}
                            fileName={`Flop 5 Breaks - ${getRadioName(breaksData?.radio_id, resolvedRadios)} from ${moment(breaksData?.start_date).format(
                                "DD-MM-YYYY",
                            )} to ${moment(breaksData?.end_date).format("DD-MM-YYYY")}`}
                            dataReady={!!breaksData}>
                            <DataDescription>
                                <DataDescription.Station radioId={breaksData?.radio_id} />
                                <DataDescription.DateRange startDate={breaksData?.start_date} endDate={breaksData?.end_date} />
                                <DataDescription.TimeRange startTime={breaksData?.start_time} endTime={breaksData?.end_time} />
                                <DataDescription.Perspective days={breaksData?.days} />
                                <DataDescription.ExcludedDays excludedRanges={daypartData?.excluded_ranges} holidays={daypartData?.holidays} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <TopFlopBreaks formData={formData} data={breaksData} loading={breaksLoading} error={breaksErrorCode} dataPath={"flop_breaks"} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                </>
            )}
        </>
    );
}
